import React, { useEffect, useRef, useState } from "react";
import "./TestRunner.scss";
import classnames from "classnames";
import { isEmpty, readableTime, sleep } from "./utils";
import { TestCase, TestResult, TestState } from "./Test";
import { LoadingIndicator } from "./LoadingIndicator";
import { useLocalStorage } from "oc-ui/react/useLocalStorage";

interface TestRunnerProps {
  testCases: TestCase[];
  testName: string;
}

export const TEST_DELIMITER = ".";

let stopped = false;

export const TestRunner: React.FC<TestRunnerProps> = ({ testCases, testName }) => {
  const [showModal, setShowModal] = useState(true);
  const [finished, setFinished] = useState(false);
  const [checkAll, setCheckAll] = useState(true);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [durationAll, setDurationAll] = useState(Number.NaN);
  const [skippedTests, setSkippedTests] = useState(0);
  const [filterTestResultState, setFilterTestResultState] = useLocalStorage(
    "TestRunnerState",
    null,
  );
  const [isMinimized, setIsMinimized] = useLocalStorage("TestRunnerMinimized", false);
  const [runTillFailed, setRunTillFailed] = useLocalStorage("TestRunnerRunTillFailed", false);
  const runTillFailedRef = useRef(runTillFailed);
  runTillFailedRef.current = runTillFailed;
  const [failedTestIndex, setFailedTestIndex] = useLocalStorage<number | null>(
    "TestRunnerFailedTestIndex",
    null,
  );
  const failedTestIndexRef = useRef(failedTestIndex);
  failedTestIndexRef.current = failedTestIndex;

  /**
   * Statt selectedTests (Whitelist) verwenden wir nun blacklistedTests.
   * Default: [] → keine Tests sind geblacklistet → alle sind ausgewählt.
   */
  const [blacklistedTests, setBlacklistedTests] = useLocalStorage<string[]>(
    "TestRunnerBlacklistedTests",
    [],
  );

  const getInitialTestResults = (testCases: TestCase[]): Record<string, TestResult> =>
    testCases.reduce(
      (obj, testCase) => ({
        ...obj,
        [testCase.name]: {
          ...testCase,
          state: undefined,
        },
      }),
      {},
    );

  const [testResults, setTestResults] = useState<Record<string, TestResult>>(
    getInitialTestResults(testCases),
  );

  /**
   * Sichtbare und nach Namen sortierte Testresultate (je nach ausgewähltem Filter).
   */
  const [visibleOrderedTestresults, setVisibleOrderedTestresults] = useState<
    [string, TestResult][]
  >([]);

  /**
   * Zähler für diverse Status-Werte
   */
  const [counts, setCounts] = useState({
    numberOfTests: 0,
    numberOfTestsDone: 0,
    numberOfTestsOk: 0,
    numberOfTestsFailed: 0,
    numberOfTestsIgnored: 0,
  });

  const addResult = (result: TestResult) => {
    console.log("testResult", result);
    setTestResults((currentTestResults) => ({
      ...currentTestResults,
      [result.name]: result,
    }));
  };

  useEffect(() => {
    const values = Object.values(testResults);
    const partition = _.groupBy(values, (t) => t.state);

    const numberOfTestsOk = partition[TestState.success]?.length ?? 0;
    const numberOfTestsFailed = partition[TestState.failed]?.length ?? 0;
    const numberOfTestsIgnored = partition[TestState.ignored]?.length ?? 0;

    setCounts({
      numberOfTests: values.length,
      numberOfTestsDone: numberOfTestsIgnored + numberOfTestsFailed + numberOfTestsOk,
      numberOfTestsOk,
      numberOfTestsFailed,
      numberOfTestsIgnored,
    });
  }, [testResults]);

  useEffect(() => {
    setVisibleOrderedTestresults(
      Object.entries(testResults)
        .filter(
          ([name, result]) =>
            filterTestResultState == null || filterTestResultState == result?.state,
        )
        .toSorted((a, b) => (a[0] < b[0] ? -1 : 1)),
    );
  }, [testResults, filterTestResultState]);

  /**
   * Wenn nicht gestoppt, automatische Ausführung.
   * checkAll ist true, wenn blacklistedTests leer ist (alle ausgewählt);
   * ansonsten false.
   */
  useEffect(() => {
    if (!stopped) {
      runSelectedTests();
    } else if (blacklistedTests.length === 0) {
      setCheckAll(true);
    } else {
      setCheckAll(false);
    }
  }, [blacklistedTests, testCases]);

  if (!showModal) {
    return null;
  }

  const getTestUrl = (testName) => {
    const url = new URL(location.href);
    url.searchParams.set("test", testName);
    return url.toString();
  };

  const {
    numberOfTests,
    numberOfTestsDone,
    numberOfTestsOk,
    numberOfTestsFailed,
    numberOfTestsIgnored,
  } = counts;

  /**
   * Checkbox-Logik:
   *  - checked bedeutet: NICHT geblacklistet
   *  - onChange toggelt → entweder blacklisten oder aus blacklist entfernen
   */
  const handleCheckboxChange = (testName: string) => {
    setBlacklistedTests(
      blacklistedTests.includes(testName)
        ? blacklistedTests.filter((name) => name !== testName) // entfernen aus blacklist
        : [...blacklistedTests, testName], // hinzufügen → unselektiert
    );
  };

  /**
   * checkAll toggelt das komplette Blacklisting.
   * Wenn checkAll=true → Klick = uncheck (→ blacklistedTests = alle → nichts ausgewählt)
   * Wenn checkAll=false → Klick = check (→ blacklistedTests = [] → alles ausgewählt)
   */
  const handleCheckAllChange = () => {
    if (checkAll) {
      // Alle unselektieren → blacklisted = ALLE
      setBlacklistedTests(
        Array.from(new Set(testCases.map((tc) => tc.name.split(TEST_DELIMITER)[0]))),
      );
    } else {
      // Alle selektieren → blacklisted = []
      setBlacklistedTests([]);
    }
    setCheckAll(!checkAll);
  };

  /**
   * Tests ausführen: sämtliche Tests, deren top-level Name NICHT in blacklistedTests enthalten ist.
   */
  const runSelectedTests = () => {
    stopped = false;
    setShowCheckboxes(false);

    const testsToRun = testCases.filter(
      (testCase) => !blacklistedTests.includes(testCase.name.split(TEST_DELIMITER)[0]),
    );
    setTestResults(getInitialTestResults(testsToRun));

    const runTests = async () => {
      window.onerror = (e) => {
        console.error(e);
      };
      (self["unhandledrejectionHandlers"] ?? []).forEach((handler) =>
        removeEventListener("unhandledrejection", handler),
      );

      APP.runningTests = true;
      const all_t0 = performance.now();
      console.log(
        "TestRunner: runTillFailed=",
        runTillFailedRef.current,
        "failedTestIndex=",
        failedTestIndexRef.current,
      );
      const startIndex =
        runTillFailedRef.current && failedTestIndexRef.current != null
          ? failedTestIndexRef.current
          : 0;
      setSkippedTests(startIndex);
      console.log("TestRunner: starting at index", startIndex);
      let hadFailure = false;
      for (let i = startIndex; i < testsToRun.length; i++) {
        const testCase = testsToRun[i];
        if (stopped) {
          break;
        }
        const t0 = performance.now();
        const handleError = (e) => {
          const t1 = performance.now();
          console.error("testResult error: ", testCase.name, e);
          addResult({
            ...testCase,
            state: TestState.failed,
            error: e["message"] ?? String(e),
            errorObject: e,
            _diffReadable: e["_diffReadable"],
            time: t1 - t0,
          });
          if (runTillFailedRef.current) {
            setFailedTestIndex(i);
            stopped = true;
            hadFailure = true;
          }
        };
        const unhandledrejectionHandler = (e) => handleError(e?.reason ?? e);
        self.addEventListener("unhandledrejection", unhandledrejectionHandler);

        try {
          console.log("TestRunner: running test", testCase.name);
          const result = await testCase.test();
          const state = result === "IGNORED" ? TestState.ignored : TestState.success;
          const t1 = performance.now();
          addResult({ ...testCase, state, time: t1 - t0 });
          // Nach jedem erfolgreichen Test den nächsten Index speichern (für Resume nach Reload)
          if (runTillFailedRef.current) {
            setFailedTestIndex(i + 1);
          }
        } catch (e) {
          handleError(e);
        } finally {
          APP.logMessagesForTests.forEach((t) => t.onClose());
          APP.logMessagesForTests = [];
          await sleep(20);
          self.removeEventListener("unhandledrejection", unhandledrejectionHandler);
        }
        // Nach Fehler mit runTillFailed sofort abbrechen
        if (stopped) {
          break;
        }
        const all_t1 = performance.now();
        setDurationAll(all_t1 - all_t0);
      }
      // Wenn alle Tests fehlerfrei durchgelaufen sind (letzter Test erreicht), Index löschen
      if (!hadFailure && runTillFailedRef.current) {
        setFailedTestIndex(null);
      }
      stopped = true;
      setFinished(true);
      APP.runningTests = false;
    };
    runTests();
  };

  return (
    <div className={"TestRunner fullsize " + (isMinimized ? "minimized" : "")}>
      <div className="bordered">
        <div className="testrunner-header">
          <div className="row">
            <h2>
              TestRunner &nbsp;&nbsp;&nbsp;&nbsp;
              <small>
                {numberOfTests === 0 ? (
                  <span className="final-result final-result-failed test-failed">
                    &nbsp; No Tests found
                  </span>
                ) : finished ? (
                  <>
                    {numberOfTestsFailed === 0 ? (
                      <span className="final-result final-result-ok test-passed">✅ all ok!</span>
                    ) : (
                      <span className="final-result final-result-failed test-failed">
                        ❌ &nbsp; {numberOfTestsFailed} Tests failed
                      </span>
                    )}
                  </>
                ) : (
                  <span className="info">pending...</span>
                )}
                &nbsp;&nbsp;
                <span className="info">
                  {[
                    <span key="1">
                      ({numberOfTestsDone + skippedTests} of {numberOfTests} Tests
                    </span>,
                    numberOfTestsFailed > 0 && (
                      <span key="2" className="test-failed">
                        {numberOfTestsFailed} failed
                      </span>
                    ),
                    numberOfTestsOk > 0 && (
                      <span key="3" className="test-passed">
                        {numberOfTestsOk} ok
                      </span>
                    ),
                    numberOfTestsIgnored > 0 && (
                      <span key="4" className="test-ignored">
                        {numberOfTestsIgnored} ignored
                      </span>
                    ),
                    <span key="5">{readableTime(durationAll)})</span>,
                  ]
                    .filter((e) => e)
                    .reduce((array, e, idx) => array.concat([idx > 0 && ", ", e]), [])}
                </span>
              </small>
            </h2>
            <div className="justify-right">
              <button
                className="test-button"
                onClick={() => {
                  stopped = true;
                  setShowCheckboxes(true);
                }}
              >
                {"< Test Selection"}
              </button>
              <select
                className="show-which-tests"
                value={filterTestResultState == null ? "all" : "failed"}
                onChange={(event) => {
                  setFilterTestResultState(
                    event.target.value === "failed" ? TestState.failed : null,
                  );
                }}
              >
                <option value="all">show all</option>
                <option value="failed">show only failed</option>
              </select>
              <label className="run-till-failed-label">
                <input
                  type="checkbox"
                  checked={runTillFailed}
                  onChange={() => {
                    if (runTillFailed) {
                      // Bei Deaktivierung Index löschen
                      setFailedTestIndex(null);
                    }
                    setRunTillFailed(!runTillFailed);
                  }}
                />
                run till failed
              </label>
              <button className="test-button" disabled={finished} onClick={() => (stopped = true)}>
                Stop
              </button>
              <div className="window-buttons">
                {!finished && <LoadingIndicator />}
                <button
                  className="window-button minimize-button"
                  onClick={() => setIsMinimized(!isMinimized)}
                  title={isMinimized ? "maximize" : "minimize"}
                >
                  {isMinimized ? "🗖" : "🗕"}
                </button>
                <button
                  className="window-button close-button"
                  onClick={() => {
                    stopped = true;
                    setShowModal(false);
                  }}
                  title="close"
                >
                  x
                </button>
              </div>
            </div>
          </div>
          {!isEmpty(testName) && (
            <div className="row navigate-buttons-row">
              <p>
                <a className="test-button" href={getTestUrl("")}>
                  &lt; run all tests
                </a>
                <a className="test-button" href={getTestUrl(testName.split(TEST_DELIMITER)[0])}>
                  &lt; run {testName.split(TEST_DELIMITER)[0]}
                </a>
              </p>
            </div>
          )}
        </div>
        <div className="viewport">
          {isEmpty(testName) && showCheckboxes ? (
            <div className="test-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="checkall"
                  checked={checkAll}
                  onChange={handleCheckAllChange}
                />{" "}
                <i>{"check/uncheck all"}</i>
              </label>
              <div className="test-selection">
                {Array.from(
                  new Set(testCases.map((testCase) => testCase.name.split(TEST_DELIMITER)[0])),
                ).map((testCaseName) => (
                  <div key={testCaseName} className="checkbox-container">
                    <label>
                      <input
                        type="checkbox"
                        checked={!blacklistedTests.includes(testCaseName)}
                        onChange={() => handleCheckboxChange(testCaseName)}
                      />
                      {testCaseName + " "}
                      <i className="test-count-info">
                        {"( " +
                          testCases.filter((tc) => tc.name.startsWith(testCaseName)).length +
                          " tests )"}
                      </i>
                    </label>
                  </div>
                ))}
              </div>
              <button
                className="test-button run-selected-tests"
                disabled={
                  // Button ist disabled, wenn alle geblacklistet sind (→ nichts ausgewählt)
                  Array.from(new Set(testCases.map((tc) => tc.name.split(TEST_DELIMITER)[0])))
                    .length === blacklistedTests.length
                }
                onClick={runSelectedTests}
              >
                Run Tests
              </button>
            </div>
          ) : (
            <>
              {testCases.length === 0 && <div>Keine Tests gefunden!</div>}
              <div className="test-result-table">
                {filterTestResultState === TestState.failed && numberOfTestsFailed === 0 && (
                  <div className="no-tests-failed">no tests failed</div>
                )}
                {visibleOrderedTestresults.map(([name, result]) => (
                  <div key={name} className="table-row">
                    <div className="table-cell">
                      <a href={getTestUrl(result.name.split(TEST_DELIMITER)[0]) + TEST_DELIMITER}>
                        {result.name.split(TEST_DELIMITER)[0]}
                      </a>
                      {TEST_DELIMITER}
                      <a href={getTestUrl(result.name)}>{result.name.split(TEST_DELIMITER)[1]}</a>
                    </div>
                    <div
                      className={classnames(
                        "table-cell",
                        result.state === TestState.success
                          ? "test-passed"
                          : result.state === TestState.ignored
                            ? "test-ignored"
                            : result.state === TestState.failed
                              ? "test-failed"
                              : "test-pending",
                      )}
                    >
                      {result.state === TestState.success
                        ? "✅ Test passed"
                        : result.state === TestState.ignored
                          ? "ignored"
                          : result.state === TestState.failed
                            ? "❌ Test failed"
                            : "pending..."}
                    </div>
                    <div className="table-cell time">{readableTime(result.time)}</div>
                    <div className="table-cell error">
                      {result.error}
                      {result._diffReadable?.()}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
